import axios from 'axios';
import { API_BASE_URL } from '@/config';

// Unified chunk size for all video uploads. Larger chunks => fewer round trips
// and faster uploads on good connections; retries keep them safe on bad ones.
export const CHUNK_SIZE = 5 * 1024 * 1024; // 5 MB

const MAX_CHUNK_RETRIES = 3;
const CHUNK_TIMEOUT_MS = 120000; // generous for slow mobile networks

// Progress is split into two phases: bytes uploading (0-90%) and server-side
// transcoding while we await the final-chunk response (90-100%).
export const PHASE_UPLOADING = 'uploading';
export const PHASE_PROCESSING = 'processing';
export const PHASE_COMPLETE = 'complete';

const UPLOAD_PROGRESS_CEILING = 90;
// While the server transcodes we have no real progress signal, so we creep the
// bar from 90 toward this ceiling and only snap to 100 once the server responds.
const PROCESSING_PROGRESS_CEILING = 99;

function getAuthHeaders() {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function buildUrl(endpoint) {
  return endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`;
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// localStorage key uniquely identifying a (file, destination) upload so an
// interrupted upload can be resumed for the same file/target.
function sessionStorageKey(file, endpoint, extraFields) {
  const target = extraFields && extraFields.video_number ? `:${extraFields.video_number}` : '';
  return `video_upload:${endpoint}${target}:${file.name}:${file.size}:${file.lastModified}`;
}

function loadSession(key) {
  try {
    return JSON.parse(localStorage.getItem(key));
  } catch (e) {
    return null;
  }
}

function saveSession(key, data) {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (e) {
    // localStorage may be unavailable/full; resume is best-effort.
  }
}

function clearSession(key) {
  try {
    localStorage.removeItem(key);
  } catch (e) {
    // ignore
  }
}

function isRetriable(error) {
  // Network errors / timeouts have no response; server (5xx) errors are
  // transient. Client errors (4xx, e.g. 422 transcode failure) are not.
  const status = error.response && error.response.status;
  if (!status) return true;
  return status >= 500;
}

// Poll the server for an upload that is being merged/transcoded out-of-band
// (the final-chunk request returned 202, or its response was lost to the
// Heroku 30s router timeout while the dyno kept working). Resolves with the
// final { status:'complete', video_path } payload, or throws a descriptive
// error shaped like an axios error so describeUploadError can format it.
const POLL_INTERVAL_MS = 3000;
const POLL_MAX_MS = 6 * 60 * 1000; // cover a ~2 min clip's transcode + S3 download/upload

function makeUploadError(message, statusCode) {
  const err = new Error(message);
  err.response = { status: statusCode, data: { error: message } };
  return err;
}

async function pollUploadStatus(statusEndpoint, uploadId) {
  const deadline = Date.now() + POLL_MAX_MS;

  while (Date.now() < deadline) {
    await delay(POLL_INTERVAL_MS);

    let data;
    try {
      const res = await axios.get(buildUrl(statusEndpoint), {
        headers: getAuthHeaders(),
        params: { upload_id: uploadId },
      });
      data = res.data || {};
    } catch (error) {
      // A 404 means the session was cleared, which the server does after a
      // failed transcode. Treat as a processing failure; keep polling on
      // transient errors.
      const code = error.response && error.response.status;
      if (code === 404) {
        throw makeUploadError(
          'We could not process this video. Try a shorter clip or a different file.',
          422,
        );
      }
      continue;
    }

    if (data.status === 'complete' && data.video_path) {
      return data;
    }
    if (data.status === 'failed') {
      throw makeUploadError(
        data.error ||
          'We could not process this video. Try a shorter clip or a different file.',
        422,
      );
    }
    // status still 'in_progress' -> keep waiting.
  }

  throw makeUploadError(
    'Your video is taking longer than expected to process. Please try again in a moment.',
    408,
  );
}

async function postChunkWithRetry(url, formData, onUploadProgress) {
  let attempt = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    try {
      return await axios.post(url, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
        timeout: CHUNK_TIMEOUT_MS,
        onUploadProgress,
      });
    } catch (error) {
      attempt += 1;
      if (!isRetriable(error) || attempt >= MAX_CHUNK_RETRIES) {
        throw error;
      }
      await delay(1000 * Math.pow(2, attempt - 1)); // 1s, 2s, 4s
    }
  }
}

/**
 * Upload a video file in chunks with retry, resume, and two-phase progress.
 *
 * @param {Object} opts
 * @param {File}   opts.file           The video file to upload.
 * @param {string} opts.endpoint       Chunk endpoint path (e.g. '/api/upload-chunk/').
 * @param {Object} [opts.extraFields]  Extra form fields per chunk (e.g. { video_number: 1 }).
 * @param {string} [opts.statusEndpoint] Optional status endpoint for server-authoritative resume.
 * @param {Function} [opts.onProgress] Called with an integer 0-100.
 * @param {Function} [opts.onPhase]    Called with PHASE_* when the phase changes.
 * @returns {Promise<Object>} The final-chunk response data ({ status, upload_id, video_path, ... }).
 */
export async function uploadVideo({
  file,
  endpoint,
  extraFields = {},
  statusEndpoint = null,
  asyncProcessing = false,
  onProgress,
  onPhase,
}) {
  const url = buildUrl(endpoint);
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
  const key = sessionStorageKey(file, endpoint, extraFields);

  let uploadId = '';
  let completed = new Set();

  // Attempt to resume a prior interrupted upload of this exact file/target.
  // We only resume when the SERVER confirms genuine partial progress, to avoid
  // trusting a stale local record (e.g. from a previous failed transcode whose
  // chunks were cleaned up server-side).
  const saved = loadSession(key);
  if (saved && saved.uploadId && saved.totalChunks === totalChunks && statusEndpoint) {
    try {
      const statusRes = await axios.get(buildUrl(statusEndpoint), {
        headers: getAuthHeaders(),
        params: { upload_id: saved.uploadId },
      });
      const data = statusRes.data || {};

      // Server already produced the final video: skip re-uploading entirely.
      if (data.status === 'complete' && data.video_path) {
        clearSession(key);
        if (onPhase) onPhase(PHASE_COMPLETE);
        if (onProgress) onProgress(100);
        return data;
      }

      // Resume only if there is partial progress that still excludes at least
      // the final chunk (the final chunk must be sent to trigger the merge).
      const received = Array.isArray(data.chunks_received) ? data.chunks_received : [];
      if (received.length > 0 && received.length < totalChunks) {
        uploadId = saved.uploadId;
        completed = new Set(received);
      }
    } catch (e) {
      // No usable server session (expired/cleared/failed): start fresh below.
    }
  }

  // Anything not confirmed as resumable starts clean.
  if (completed.size === 0) {
    uploadId = '';
    clearSession(key);
  }

  const emitProgress = (value) => {
    if (onProgress) onProgress(Math.min(100, Math.max(0, Math.round(value))));
  };
  const emitPhase = (phase) => {
    if (onPhase) onPhase(phase);
  };

  // Simulated progress for the transcoding phase: once the final chunk's bytes
  // are sent, the server transcodes synchronously while we await its response.
  // There's no real progress to report, so ease the bar from 90 toward 99 (it
  // decelerates and never quite reaches it) until the response snaps us to 100.
  let processingTimer = null;
  let processingValue = UPLOAD_PROGRESS_CEILING;
  const startProcessingSimulation = () => {
    if (processingTimer) return;
    processingValue = UPLOAD_PROGRESS_CEILING;
    processingTimer = setInterval(() => {
      processingValue += (PROCESSING_PROGRESS_CEILING - processingValue) * 0.07;
      emitProgress(processingValue);
    }, 500);
  };
  const stopProcessingSimulation = () => {
    if (processingTimer) {
      clearInterval(processingTimer);
      processingTimer = null;
    }
  };

  emitPhase(PHASE_UPLOADING);

  let finalResponse = null;
  // Tracks whether the final chunk's bytes were fully delivered, so a later
  // error can be treated as "server is processing" (poll) vs a genuine network
  // failure mid-transfer (fail/propagate).
  let finalBytesSent = false;

  try {
    for (let chunkNumber = 1; chunkNumber <= totalChunks; chunkNumber += 1) {
      const isLast = chunkNumber === totalChunks;

      if (completed.has(chunkNumber)) {
        emitProgress((completed.size / totalChunks) * UPLOAD_PROGRESS_CEILING);
        continue;
      }

      const start = (chunkNumber - 1) * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);

      const formData = new FormData();
      formData.append('chunk', chunk);
      formData.append('chunk_number', chunkNumber);
      formData.append('total_chunks', totalChunks);
      formData.append('filename', file.name);
      if (uploadId) {
        formData.append('upload_id', uploadId);
      }
      // Opt into server-side async (background-thread) transcoding. Only sent by
      // clients that poll upload-status; older clients omit it and the server
      // keeps the legacy synchronous response for backward compatibility.
      if (asyncProcessing && isLast) {
        formData.append('async_processing', '1');
      }
      Object.keys(extraFields).forEach((field) => {
        formData.append(field, extraFields[field]);
      });

      const onUploadProgress = (progressEvent) => {
        const chunkFraction = progressEvent.total
          ? progressEvent.loaded / progressEvent.total
          : 0;
        const overall =
          ((completed.size + chunkFraction) / totalChunks) * UPLOAD_PROGRESS_CEILING;
        emitProgress(overall);

        // Once the final chunk's bytes are fully sent, the server is transcoding.
        if (isLast && chunkFraction >= 1) {
          finalBytesSent = true;
          emitPhase(PHASE_PROCESSING);
          emitProgress(UPLOAD_PROGRESS_CEILING);
          startProcessingSimulation();
        }
      };

      // The final chunk triggers a long server-side merge+transcode. We must
      // NOT blindly retry it (a retry races the first run's chunk cleanup and
      // surfaces as "missing chunk N"). Instead, on a router timeout / 202 we
      // poll upload-status until the in-flight processing finishes.
      if (isLast) {
        let data = null;
        try {
          const resp = await axios.post(url, formData, {
            headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
            timeout: CHUNK_TIMEOUT_MS,
            onUploadProgress,
          });
          data = resp.data || {};
        } catch (error) {
          // Bytes were delivered but the response was lost (e.g. Heroku 30s
          // router timeout) — the dyno is still processing. Poll instead of
          // re-sending. A pre-byte-completion failure is a real network error.
          if (finalBytesSent && uploadId && statusEndpoint && isRetriable(error)) {
            finalResponse = await pollUploadStatus(statusEndpoint, uploadId);
            break;
          }
          throw error;
        }

        if (data.upload_id) {
          uploadId = data.upload_id;
        }

        if (data.status === 'complete' && data.video_path) {
          finalResponse = data;
          break;
        }

        // Server accepted the upload but is still merging/transcoding (202).
        if (uploadId && statusEndpoint) {
          finalResponse = await pollUploadStatus(statusEndpoint, uploadId);
          break;
        }

        throw new Error('Upload did not complete: server never returned a final video path.');
      }

      const response = await postChunkWithRetry(url, formData, onUploadProgress);

      if (response.data && response.data.upload_id) {
        uploadId = response.data.upload_id;
      }

      completed.add(chunkNumber);
      saveSession(key, {
        uploadId,
        totalChunks,
        completedChunks: Array.from(completed),
      });

      if (response.data && response.data.status === 'complete') {
        finalResponse = response.data;
        break;
      }
    }
  } finally {
    stopProcessingSimulation();
  }

  if (!finalResponse) {
    throw new Error('Upload did not complete: server never returned a final video path.');
  }

  clearSession(key);
  emitPhase(PHASE_COMPLETE);
  emitProgress(100);
  return finalResponse;
}

/**
 * Extract a human-friendly message from an upload error, distinguishing a
 * server-side processing/transcode failure (422) from a network failure.
 */
export function describeUploadError(error) {
  const data = error && error.response && error.response.data;
  if (data && data.error) return data.error;
  if (error && error.response && error.response.status === 422) {
    return 'We could not process this video. Try a shorter clip or a different file.';
  }
  return 'Upload failed due to a network problem. Please check your connection and try again.';
}
